import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Eye, Loader2, Play } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";
import ConfirmationModal from "../components/common/ConfirmationModal";
import TypedCountConfirmation, {
  needsTypedConfirmation,
  typedCountMatches,
} from "../components/common/TypedCountConfirmation";
import ConflictRuleForm, { renderedRuleFields } from "../components/ConflictRules/ConflictRuleForm";
import ConflictRuleLibraryPanel from "../components/ConflictRules/ConflictRuleLibraryPanel";
import RuleDiagnosticList from "../components/ConflictRules/RuleDiagnosticList";
import RulePreviewPanel from "../components/ConflictRules/RulePreviewPanel";
import RuleRunPanel, { RuleRunState } from "../components/ConflictRules/RuleRunPanel";
import { QUERY_KEYS } from "../constants/QueryKeys";
import { plural } from "../helpers/CommonHelpers";
import { invalidateConflictDecisionQueries } from "../lib/conflictQueries";
import { RuleDiagnostics } from "../lib/conflictRuleDiagnostics";
import { parseConflictRule } from "../lib/schemas/conflictRuleSchema";
import ConflictRulesRepository, { ConflictRuleRequestError } from "../repositories/ConflictRulesRepository";
import OrganizationsRepository from "../repositories/OrganizationsRepository";
import { ConflictReasonLabels } from "../models/ConflictReason";
import { ConflictResolutionActionLabels } from "../models/ConflictResolutionAction";
import {
  ConflictRuleDefinition,
  ConflictRulePreview,
  ConflictRuleProgress,
  ConflictRuleValidation,
  emptyConflictRule,
  LoadConflictRuleState,
  SavedConflictRule,
  serializeConflictRule,
} from "../models/ConflictRule";
import { MigrationTypeLabels } from "../models/Statistics/MigrationType";
import { UserRole } from "../models/User";

/** How long the form waits after the last edit before it asks the server to check the rule. */
const VALIDATE_DEBOUNCE_MS = 400;

/** The run's reason, as the server bounds it. */
const MIN_REASON_LENGTH = 10;
const MAX_REASON_LENGTH = 500;

interface ResolveConflictsByRulePageProps {
  userRole: UserRole;
  organizationName?: string;
  organizationId: string;
}

/** The rule the page opens with: one handed over in router state (a past run's), or an empty one. */
const initialRule = (state: unknown): { rule: ConflictRuleDefinition; problem: string | null } => {
  const handed = (state as Partial<LoadConflictRuleState> | null)?.conflictRule;
  if (typeof handed !== "string") return { rule: emptyConflictRule(), problem: null };
  const parsed = parseConflictRule(handed);
  return parsed.ok ? { rule: parsed.rule, problem: null } : { rule: emptyConflictRule(), problem: parsed.message };
};

/**
 * Resolve Conflicts by Rule: build a rule that picks the winner of each conflict group and what its other copies get,
 * preview what it would decide across the organization, then run it. One action slot leads through the steps:
 * Preview while the rule has no preview, Run once the rule in the form has one, and back to Preview on any edit.
 */
const ResolveConflictsByRulePage: React.FC<ResolveConflictsByRulePageProps> = ({ organizationName, organizationId }) => {
  const repository = new ConflictRulesRepository();
  const organizationsRepository = new OrganizationsRepository();
  const queryClient = useQueryClient();
  const location = useLocation();

  const [opening] = useState(() => initialRule(location.state));
  const [rule, setRule] = useState<ConflictRuleDefinition>(opening.rule);
  // Bumped whenever the rule is replaced from outside the form, so the form drops what it holds of the old one.
  const [formKey, setFormKey] = useState(0);
  const [loadedRule, setLoadedRule] = useState<SavedConflictRule | null>(null);
  const ruleText = useMemo(() => serializeConflictRule(rule), [rule]);

  const [validation, setValidation] = useState<{ ruleText: string; result: ConflictRuleValidation } | null>(null);
  // Bumped by "Check again", so a check that failed is retried without an edit to the rule.
  const [validationAttempt, setValidationAttempt] = useState(0);
  const [validationFailure, setValidationFailure] = useState<{
    ruleText: string;
    attempt: number;
    message: string;
  } | null>(null);

  const [previewing, setPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState<ConflictRuleProgress | null>(null);
  const [preview, setPreview] = useState<{ ruleText: string; result: ConflictRulePreview } | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const previewAbortRef = useRef<AbortController | null>(null);

  const [run, setRun] = useState<RuleRunState | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [reason, setReason] = useState("");

  // The results render below the form, usually out of view, so a preview or run that starts or ends brings them into
  // view: without it a finished run looks as if nothing happened.
  const resultsRef = useRef<HTMLDivElement>(null);
  const [revealCount, setRevealCount] = useState(0);
  const revealResults = () => setRevealCount((count) => count + 1);
  useEffect(() => {
    const results = resultsRef.current;
    if (revealCount === 0 || !results) return;
    results.focus({ preventScroll: true });
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    results.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
  }, [revealCount]);
  const [typedCount, setTypedCount] = useState("");

  useEffect(() => {
    if (opening.problem) toast.error(`The rule could not be loaded. ${opening.problem}`);
  }, [opening]);

  const catalogQuery = useQuery({
    queryKey: [QUERY_KEYS.CONFLICT_RULE_CATALOG],
    queryFn: () => repository.getCatalog(),
    staleTime: Infinity,
  });

  // Checked on the server, which is what decides whether a preview, a run or a save is refused, so the form never
  // disagrees with it. Only the newest text's answer is kept.
  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const result = await repository.validate(ruleText, controller.signal);
        setValidation({ ruleText, result });
        setValidationFailure(null);
      } catch (error) {
        if (!controller.signal.aborted)
          setValidationFailure({ ruleText, attempt: validationAttempt, message: (error as Error).message });
      }
    }, VALIDATE_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // The repository is rebuilt on every render; only the rule text and a retry decide when to check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ruleText, validationAttempt]);

  // A preview reads only, so it is not worth finishing once nobody is looking at the page.
  useEffect(() => () => previewAbortRef.current?.abort(), []);

  const running = run?.status === "running";

  // Leaving the tab cancels the request, which stops the run at its next step. Committed chunks stay, so this only
  // asks the person to confirm.
  useEffect(() => {
    if (!running) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [running]);

  // Field hints stay on the last answer while the next check is pending, so they do not blink on every keystroke.
  const diagnostics = useMemo(() => new RuleDiagnostics(validation?.result), [validation]);
  const currentValidation = validation?.ruleText === ruleText ? validation.result : null;
  // The alert above the form reads only the answer for the text in the form: an older answer can name a condition row
  // that has since been removed or hidden, which no field shows any more.
  const currentDiagnostics = useMemo(() => new RuleDiagnostics(currentValidation), [currentValidation]);
  // A failure says nothing about text the form has moved on from, nor about a check started again.
  const currentValidationFailure =
    validationFailure?.ruleText === ruleText && validationFailure.attempt === validationAttempt
      ? validationFailure.message
      : null;
  const ruleHasErrors = !currentValidation || currentValidation.errors.length > 0;
  const currentPreview = preview?.ruleText === ruleText ? preview.result : null;
  const canPreview = !!catalogQuery.data && !ruleHasErrors && !previewing && !running;

  const applyRuleText = (text: string, savedRule: SavedConflictRule | null) => {
    const parsed = parseConflictRule(text);
    if (!parsed.ok) {
      toast.error(parsed.message);
      return;
    }
    setRule(parsed.rule);
    setFormKey((key) => key + 1);
    setLoadedRule(savedRule);
  };

  /** A refusal that names parts of the rule is shown on the form, as a validation would. */
  const pinRefusal = (text: string, error: ConflictRuleRequestError) => {
    if (error.diagnostics.length > 0) setValidation({ ruleText: text, result: { errors: error.diagnostics, warnings: [] } });
  };

  const handlePreview = async () => {
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    const text = ruleText;

    setPreviewing(true);
    setPreviewProgress(null);
    setRun(null);
    revealResults();
    try {
      const result = await repository.preview(text, setPreviewProgress, controller.signal);
      setPreview({ ruleText: text, result });
      revealResults();
    } catch (error) {
      if (controller.signal.aborted) return;
      if (error instanceof ConflictRuleRequestError) pinRefusal(text, error);
      toast.error((error as Error).message);
    } finally {
      if (previewAbortRef.current === controller) {
        setPreviewing(false);
        setPreviewProgress(null);
      }
    }
  };

  const handleDownloadPreviewCsv = async () => {
    setDownloadingCsv(true);
    try {
      await repository.downloadPreviewCsv(ruleText);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setDownloadingCsv(false);
    }
  };

  /**
   * Each decided file only marks its entity's statistics dirty, so the counts are recalculated before the conflict
   * queries are invalidated: the other way round would cache the counts from before the run as fresh.
   */
  const refreshAfterRun = async () => {
    try {
      await organizationsRepository.recalculateDirtyOrganizationStatistics(organizationId);
    } catch {
      toast.warning("Conflicts decided, but their statistics could not be refreshed now.", {
        description: "The counts catch up on the next scheduled recalculation.",
        duration: 10000,
      });
    }
    await invalidateConflictDecisionQueries(queryClient);
  };

  const handleRun = async () => {
    if (!currentPreview) return;
    const confirmedFileCount = currentPreview.filesToDecide;
    const text = ruleText;

    // A preview still streaming would store counts from before the run once it ends, after the run has cleared them.
    previewAbortRef.current?.abort();
    setConfirming(false);
    setTypedCount("");
    setRun({ status: "running", confirmedFileCount, progress: null });
    revealResults();

    let outcome: RuleRunState;
    try {
      const result = await repository.execute(
        { rule: text, reason: reason.trim(), confirmedFileCount },
        (progress) => setRun({ status: "running", confirmedFileCount, progress })
      );
      outcome = { status: "finished", result, refreshing: true };
      toast.success(
        `The rule decided ${plural(result.filesDecided, "file")} in ${plural(result.groupsDecided, "group")}.`
      );
    } catch (error) {
      if (error instanceof ConflictRuleRequestError) {
        // Refused before the first decision, so nothing changed.
        setRun(null);
        if (error.status === 409) {
          setPreview(null);
          toast.error("Counts changed since the preview. Preview again.");
        } else {
          pinRefusal(text, error);
          toast.error(error.message);
        }
        return;
      }
      outcome = { status: "failed", error: (error as Error).message, refreshing: true };
    }

    // The next run is asked for its own reason. A refusal returned above and keeps it, so retrying the same run does not
    // mean typing it again.
    setReason("");

    // The groups it decided are no longer candidates, so the preview no longer says what the rule would do.
    setPreview(null);
    setRun(outcome);
    revealResults();
    await refreshAfterRun();
    // A preview started meanwhile has cleared the panel, and keeps it clear.
    setRun((current) => (current === outcome ? { ...outcome, refreshing: false } : current));
  };

  const trimmedReason = reason.trim();
  const reasonValid = trimmedReason.length >= MIN_REASON_LENGTH && trimmedReason.length <= MAX_REASON_LENGTH;
  const confirmCount = currentPreview?.filesToDecide ?? 0;
  const typedConfirmationNeeded = needsTypedConfirmation(confirmCount);
  const outsideDiagnostics = currentDiagnostics.outside(renderedRuleFields(rule));
  const optionsTicked = rule.options.allowReplacingCopiesInSharePoint || rule.options.allowAcceptingDifferentContent;

  // A preview in progress holds the slot even when an older preview of the same rule exists: the panel no longer
  // shows the counts a Run would be confirmed with.
  const actionSlot = running ? (
    <Button disabled className="gap-2">
      <Loader2 className="size-4 animate-spin" />
      Running…
    </Button>
  ) : previewing ? (
    <Button disabled className="gap-2">
      <Loader2 className="size-4 animate-spin" />
      Preview
    </Button>
  ) : currentPreview && currentPreview.filesToDecide > 0 ? (
    <Button className="gap-2" onClick={() => setConfirming(true)}>
      <Play className="size-4" />
      Run on {currentPreview.filesToDecide.toLocaleString()} {currentPreview.filesToDecide === 1 ? "file" : "files"}
    </Button>
  ) : (
    <Button className="gap-2" onClick={handlePreview} disabled={!canPreview}>
      <Eye className="size-4" />
      Preview
    </Button>
  );

  const slotHint: React.ReactNode = running
    ? null
    : currentValidationFailure
      ? (
        <>
          The rule could not be checked: {currentValidationFailure}{" "}
          <Button
            variant="link"
            className="h-auto p-0 text-xs"
            onClick={() => setValidationAttempt((attempt) => attempt + 1)}
          >
            Check again
          </Button>
        </>
      )
      : !currentValidation
        ? "Checking the rule…"
        : currentValidation.errors.length > 0
          ? "Fix the rule's errors to preview it."
          : previewing
            ? "A preview changes nothing."
            : currentPreview && currentPreview.filesToDecide === 0
              ? "The rule decides no group now."
              : currentPreview
                ? "Any change to the rule needs a new preview before it runs."
                : "A preview changes nothing.";

  return (
    // The dashboard clips its content area, so a page taller than the window scrolls only inside a box of its own.
    <div className="w-full flex-1 min-h-0 overflow-y-auto">
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="mt-2 flex flex-col gap-1">
            <h2 className="flex items-center gap-2 text-2xl font-bold tracking-tight">
              Resolve Conflicts by Rule
              <DocsHelpLink to="how-to/resolve-conflicts-by-rule" />
            </h2>
            <p className="text-sm text-muted-foreground">
              Decide many conflict groups at once: the rule picks each group's winner by where its copies come from, and
              what the other copies get. Groups the rule cannot decide safely are skipped and listed.
            </p>
          </div>

          {outsideDiagnostics.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertTitle>The rule cannot be read as it is</AlertTitle>
              <AlertDescription>
                <RuleDiagnosticList diagnostics={outsideDiagnostics} />
              </AlertDescription>
            </Alert>
          )}

          {catalogQuery.isLoading && <Skeleton className="h-96 w-full" />}
          {catalogQuery.isError && (
            <Alert variant="destructive">
              <AlertTriangle className="size-4" />
              <AlertDescription>{(catalogQuery.error as Error).message}</AlertDescription>
            </Alert>
          )}
          {catalogQuery.data && (
            <ConflictRuleForm
              key={formKey}
              rule={rule}
              onChange={setRule}
              catalog={catalogQuery.data}
              diagnostics={diagnostics}
              disabled={running}
            />
          )}

          <div className="flex flex-wrap items-center gap-3">
            {actionSlot}
            {slotHint && <span className="text-xs text-muted-foreground">{slotHint}</span>}
          </div>

          <div
            ref={resultsRef}
            tabIndex={-1}
            role="region"
            aria-label="Rule results"
            className="flex scroll-mt-2 flex-col gap-4 outline-none empty:hidden"
          >
            {run && <RuleRunPanel run={run} organizationId={organizationId} />}

            <RulePreviewPanel
              progress={previewProgress}
              previewing={previewing}
              preview={preview?.result ?? null}
              stale={!!preview && preview.ruleText !== ruleText}
              onPreviewAgain={handlePreview}
              canPreview={canPreview}
              onDownloadCsv={handleDownloadPreviewCsv}
              downloadingCsv={downloadingCsv}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-2">
          <ConflictRuleLibraryPanel
            ruleText={ruleText}
            ruleName={rule.name}
            saveBlocked={!currentValidation ? "checking" : currentValidation.errors.length > 0 ? "errors" : null}
            loadedRule={loadedRule}
            onUse={applyRuleText}
            onSaved={setLoadedRule}
            onDeleted={(id) => setLoadedRule((current) => (current?.id === id ? null : current))}
            disabled={running}
          />
        </div>

        {confirming && currentPreview && (
          <ConfirmationModal
            open
            onClose={() => setConfirming(false)}
            onConfirm={handleRun}
            message={`Run this rule on ${confirmCount.toLocaleString()} ${confirmCount === 1 ? "file" : "files"}? It checks every group again first, and changes nothing if it no longer decides that many files.`}
            statusColor={optionsTicked ? "danger" : "warning"}
            buttonText="Run"
            maxWidth="36rem"
            confirmDisabled={!reasonValid || (typedConfirmationNeeded && !typedCountMatches(typedCount, confirmCount))}
          >
            <div className="flex flex-col gap-3 text-sm">
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                <dt className="font-semibold">Organization</dt>
                <dd className="break-words text-muted-foreground">
                  {organizationName || "Unknown"} <span className="font-mono text-xs">({organizationId})</span>
                </dd>
                <dt className="font-semibold">Rule</dt>
                <dd className="break-words text-muted-foreground">{rule.name?.trim() || "Unnamed rule"}</dd>
                <dt className="font-semibold">Scope</dt>
                <dd className="text-muted-foreground">
                  {rule.appliesTo.families.map((family) => MigrationTypeLabels[family]).join(", ")} ·{" "}
                  {rule.appliesTo.reasons.map((reason) => ConflictReasonLabels[reason]).join(", ")}
                  {rule.appliesTo.caseNumbers.length > 0 && ` · ${plural(rule.appliesTo.caseNumbers.length, "listed case number")}`}
                </dd>
                <dt className="font-semibold">Decides</dt>
                <dd className="text-muted-foreground">
                  {plural(currentPreview.groupsToDecide, "group")}:{" "}
                  {currentPreview.actions
                    .map((summary) => `${summary.fileCount.toLocaleString()} ${ConflictResolutionActionLabels[summary.action]}`)
                    .join(", ")}
                </dd>
                {currentPreview.copiesThatWait > 0 && (
                  <>
                    <dt className="font-semibold">Will wait</dt>
                    <dd className="text-muted-foreground">
                      {plural(currentPreview.copiesThatWait, "copy", "copies")}, until{" "}
                      {currentPreview.copiesThatWait === 1 ? "its" : "their"} winner is in SharePoint
                    </dd>
                  </>
                )}
                <dt className="font-semibold">Skips</dt>
                <dd className="text-muted-foreground">
                  {plural(currentPreview.groupsSkipped, "group")}, listed in the run's audit entry
                </dd>
              </dl>

              {currentPreview.warnings.length > 0 && (
                <RuleDiagnosticList
                  diagnostics={currentPreview.warnings.map((warning) => ({ ...warning, severity: "warning" as const }))}
                />
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="conflict-rule-run-reason" className="after:ml-0.5 after:text-destructive after:content-['*']">
                  Reason
                </Label>
                <Input
                  id="conflict-rule-run-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  maxLength={MAX_REASON_LENGTH}
                  placeholder="Why this rule runs now (at least 10 characters)"
                  aria-invalid={reason.length > 0 && !reasonValid}
                />
                <span className="text-xs text-muted-foreground">Recorded in the run's audit entry.</span>
              </div>

              {typedConfirmationNeeded && (
                <TypedCountConfirmation fileCount={confirmCount} value={typedCount} onChange={setTypedCount} />
              )}
            </div>
          </ConfirmationModal>
        )}
      </div>
    </div>
  );
};

export default ResolveConflictsByRulePage;
